import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import OrgIcon from "../../../icons/OverviewOrgIcon.svg";
import ThemeIcon from "../../../icons/OverviewThemeIcon.svg";
import Calendar from "../../../icons/OverviewOrgCalendar.svg";
import AuditsIcon from "../../../icons/OverviewAuditsIcon.svg";

interface NodeDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    nodeData: {
        nodeType: string;
        name: string;
        children?: any[];
        details?: any;
        overallComplianceRate?: number;
    } | null;
    onNodeExpand?: (node: any) => void;
    viewMode?: 'organization' | 'theme';
}

const NodeDetailModal: React.FC<NodeDetailModalProps> = ({ isOpen, onClose, nodeData, onNodeExpand }) => {
    const [navigationStack, setNavigationStack] = useState<any[]>([]);
    const [currentNode, setCurrentNode] = useState<any>(null);

    // Initialize current node when modal opens
    useEffect(() => {
        if (isOpen && nodeData) {
            setCurrentNode(nodeData);
            setNavigationStack([nodeData]);
        }
    }, [isOpen, nodeData]);

    if (!isOpen || !currentNode) return null;

    // Format year from ISO date string
    const formatYear = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.getFullYear().toString();
        } catch {
            return dateString;
        }
    };

    // Handle item click (for navigation/drilldown)
    const handleItemClick = (item: any) => {
        setNavigationStack([...navigationStack, item]);
        setCurrentNode(item);

        // Trigger node expansion in the graph
        if (onNodeExpand) {
            onNodeExpand(item);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (navigationStack.length > 1) {
            const newStack = navigationStack.slice(0, -1);
            setNavigationStack(newStack);
            setCurrentNode(newStack[newStack.length - 1]);

            // Trigger node collapse in the graph - collapse the current node
            if (onNodeExpand && currentNode) {
                // Pass a special flag to indicate collapse
                onNodeExpand({ ...currentNode, _shouldCollapse: true });
            }
        }
    };

    // Handle close - reset navigation
    const handleClose = () => {
        setNavigationStack([]);
        setCurrentNode(null);
        onClose();
    };

    // Calculate compliance metrics
    const calculateMetrics = (node: any) => {
        let totalAudits = 0;
        let compliantCount = 0;
        let nonCompliantCount = 0;
        const years = new Set<string>();

        const traverse = (n: any) => {
            if (n.nodeType === 'AUDIT') {
                totalAudits++;
            } else if (n.nodeType === 'YEAR') {
                years.add(formatYear(n.name));
            } else if (n.nodeType === 'COMPLIANT') {
                compliantCount += parseInt(n.name) || 0;
            } else if (n.nodeType === 'NON_COMPLIANT') {
                nonCompliantCount += parseInt(n.name) || 0;
            }
            n.children?.forEach((child: any) => traverse(child));
        };

        traverse(node);

        // Use API's overallComplianceRate if available, otherwise calculate it
        let complianceRate;
        if (node.overallComplianceRate !== undefined && node.overallComplianceRate !== null) {
            // Format the double value from API
            // Remove unnecessary decimals for whole numbers (e.g., 0.0 -> 0, 100.0 -> 100, 95.5 -> 95.5)
            const roundedRate = Number(node.overallComplianceRate.toFixed(1));
            complianceRate = roundedRate % 1 === 0 ? Math.round(roundedRate) : roundedRate;
        } else {
            // Fallback to calculated rate
            complianceRate = totalAudits > 0 ? Math.round((compliantCount / (compliantCount + nonCompliantCount)) * 100) : 0;
        }

        return {
            totalAudits,
            complianceRate,
            years: Array.from(years).sort()
        };
    };

    const renderContent = () => {
        switch (currentNode.nodeType) {
            case 'ORGANIZATION': {
                // Check if this is root organization or organization under theme
                // by checking the children's nodeType
                const hasThemeChildren = currentNode.children?.[0]?.nodeType === 'THEME';

                if (hasThemeChildren) {
                    // Root ORGANIZATION → THEME structure
                    const metrics = calculateMetrics(nodeData);

                    return (
                        <div className={styles.content}>
                            {/* Header with icon and title */}
                            <div className={styles.orgHeader}>
                                <div className={styles.orgIconWrapper}>
                                    <img src={OrgIcon} alt="Organization" style={{ width: '24px', height: '24px' }} />
                                </div>
                                <div className={styles.orgTitleSection}>
                                    <div className={styles.orgLabel}>{currentNode.name}</div>
                                    <div className={styles.orgTitle}>Organization Overview</div>
                                </div>
                                <button className={styles.closeButtonWhite} onClick={handleClose}>×</button>
                            </div>

                            {/* Compliance Rate Section */}
                            <div className={styles.complianceSection}>
                                <div className={styles.complianceHeader}>
                                    <span className={styles.complianceLabel}>Overall Compliance Rate</span>
                                    <span className={styles.compliancePercent}>{metrics.complianceRate}%</span>
                                </div>
                                <div className={styles.complianceProgressBar}>
                                    <div
                                        className={styles.complianceProgressFill}
                                        style={{ width: `${metrics.complianceRate}%` }}
                                    />
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className={styles.statsGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statLabel}>Total Audits</div>
                                    <div className={styles.statValue}>{metrics.totalAudits}</div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statLabel}>Years Available</div>
                                    <div className={styles.statValue}>{metrics.years.join(', ')}</div>
                                </div>
                            </div>

                            {/* Themes Section */}
                            <div className={styles.themesSection}>
                                <div className={styles.themesHeader}>Themes ({currentNode.children?.length || 0})</div>
                                <div className={styles.themesList}>
                                    {currentNode.children?.map((child: any, index: number) => (
                                        <div key={index} className={styles.themeCard} onClick={() => handleItemClick(child)}
                                         onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleItemClick(child);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}
                                             >                                        <div className={styles.themeIconWrapper}>
                                                <img src={ThemeIcon} alt="Theme" style={{ width: '20px', height: '20px' }} />
                                            </div>
                                            <span className={styles.themeName}>{child.name}</span>
                                            <svg className={styles.themeArrow} width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                } else {
                    // ORGANIZATION under THEME → YEAR structure
                    const orgMetrics = calculateMetrics(currentNode);
                    const showBackButton = navigationStack.length > 1;

                    return (
                        <div className={styles.content}>
                            {/* Organization Header */}
                            <div className={styles.orgHeader}>
                                <div className={styles.orgIconWrapper}>
                                    <img src={OrgIcon} alt="Organization" style={{ width: '24px', height: '24px' }} />
                                </div>
                                <div className={styles.orgTitleSection}>
                                    <div className={styles.orgLabel}>{currentNode.name}</div>
                                    <div className={styles.orgTitle}>Organization Overview</div>
                                </div>
                                <button className={styles.closeButtonWhite} onClick={handleClose}>×</button>
                            </div>

                            {/* Back to Theme */}
                            {showBackButton && (
                                <div className={styles.backSection} onClick={handleBack}
                                onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleBack();
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 12L6 8L10 4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Back to Theme</span>
                                </div>
                            )}

                            {/* Compliance Rate Section */}
                            <div className={styles.complianceSection}>
                                <div className={styles.complianceHeader}>
                                    <span className={styles.complianceLabel}>Overall Compliance Rate</span>
                                    <span className={styles.compliancePercent}>{orgMetrics.complianceRate}%</span>
                                </div>
                                <div className={styles.complianceProgressBar}>
                                    <div
                                        className={styles.complianceProgressFill}
                                        style={{ width: `${orgMetrics.complianceRate}%` }}
                                    />
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className={styles.statsGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statLabel}>Total Audits</div>
                                    <div className={styles.statValue}>{orgMetrics.totalAudits}</div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statLabel}>Years Available</div>
                                    <div className={styles.statValue}>{orgMetrics.years.join(', ')}</div>
                                </div>
                            </div>

                            {/* Years Section */}
                            <div className={styles.themesSection}>
                                <div className={styles.themesHeader}>Years ({currentNode.children?.length || 0})</div>
                                <div className={styles.themesList}>
                                    {currentNode.children?.map((child: any, index: number) => {
                                        const auditCount = child.children?.length || 0;
                                        return (
                                            <div key={index} className={styles.themeCard} onClick={() => handleItemClick(child)}
                                              onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleItemClick(child);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}
                                             >
                                                <div className={styles.yearIconWrapper}>
                                                    <img src={Calendar} alt="Year" style={{ width: '20px', height: '20px' }} />
                                                </div>
                                                <div className={styles.yearInfo}>
                                                    <div className={styles.yearName}>{formatYear(child.name)}</div>
                                                    <div className={styles.yearAuditCount}>{auditCount} {auditCount === 1 ? 'Audit' : 'Audits'}</div>
                                                </div>
                                                <svg className={styles.themeArrow} width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                }
            }

            case 'THEME': {
                // Check if children are ORGANIZATION or YEAR nodes
                const isRootTheme = currentNode.children?.[0]?.nodeType === 'ORGANIZATION';

                if (isRootTheme) {
                    // THEME → ORGANIZATION structure (when Theme is root)
                    return (
                        <div className={styles.content}>
                            {/* Theme Header */}
                            <div className={styles.orgHeader}>
                                <div className={styles.orgIconWrapper}>
                                    <img src={ThemeIcon} alt="Theme" style={{ width: '24px', height: '24px' }} />
                                </div>
                                <div className={styles.orgTitleSection}>
                                    <div className={styles.orgLabel}>{currentNode.name}</div>
                                    <div className={styles.orgTitle}>Theme Overview</div>
                                </div>
                                <button className={styles.closeButtonWhite} onClick={handleClose}>×</button>
                            </div>

                            {/* Organizations Section */}
                            <div className={styles.themesSection}>
                                <div className={styles.themesHeader}>Organizations ({currentNode.children?.length || 0})</div>
                                <div className={styles.themesList}>
                                    {currentNode.children?.map((child: any, index: number) => (
                                        <div key={index} className={styles.themeCard} onClick={() => handleItemClick(child)}
                                        onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleItemClick(child);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                            <div className={styles.orgIconWrapperSmall}>
                                                <img src={OrgIcon} alt="Organization" style={{ width: '20px', height: '20px' }} />
                                            </div>
                                            <div className={styles.yearInfo}>
                                                <div className={styles.yearName}>{child.name}</div>
                                            </div>
                                            <svg className={styles.themeArrow} width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                } else {
                    // THEME → YEAR structure (when Organization is root)
                    const showBackButton = navigationStack.length > 1;

                    return (
                        <div className={styles.content}>
                            {/* Theme Header */}
                            <div className={styles.orgHeader}>
                                <div className={styles.orgIconWrapper}>
                                    <img src={ThemeIcon} alt="Theme" style={{ width: '24px', height: '24px' }} />
                                </div>
                                <div className={styles.orgTitleSection}>
                                    <div className={styles.orgLabel}>{currentNode.name}</div>
                                    <div className={styles.orgTitle}>Theme Overview</div>
                                </div>
                                <button className={styles.closeButtonWhite} onClick={handleClose}>×</button>
                            </div>

                            {/* Back to Organization */}
                            {showBackButton && (
                                <div className={styles.backSection} onClick={handleBack}
                                onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleBack();
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 12L6 8L10 4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Back to Organization</span>
                                </div>
                            )}

                            {/* Years Section */}
                            <div className={styles.themesSection}>
                                <div className={styles.themesHeader}>Years ({currentNode.children?.length || 0})</div>
                                <div className={styles.themesList}>
                                    {currentNode.children?.map((child: any, index: number) => {
                                        const auditCount = child.children?.length || 0;
                                        return (
                                            <div key={index} className={styles.themeCard} onClick={() => handleItemClick(child)}
                                            onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleItemClick(child);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                                <div className={styles.yearIconWrapper}>
                                                    <img src={Calendar} alt="Year" style={{ width: '20px', height: '20px' }} />
                                                </div>
                                                <div className={styles.yearInfo}>
                                                    <div className={styles.yearName}>{formatYear(child.name)}</div>
                                                    <div className={styles.yearAuditCount}>{auditCount} {auditCount === 1 ? 'Audit' : 'Audits'}</div>
                                                </div>
                                                <svg className={styles.themeArrow} width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                }
            }

            case 'YEAR': {
                // Determine back text based on parent node type
                const parentNode = navigationStack[navigationStack.length - 2];
                const backText = parentNode?.nodeType === 'ORGANIZATION'
                    ? 'Back to Organization'
                    : parentNode?.nodeType === 'THEME'
                    ? 'Back to Theme'
                    : 'Back';
                const showYearBackButton = navigationStack.length > 1;

                return (
                    <div className={styles.content}>
                        {/* Year Header */}
                        <div className={styles.orgHeader}>
                            <div className={styles.orgIconWrapper}>
                                <img src={Calendar} alt="Year" style={{ width: '24px', height: '24px' }} />
                            </div>
                            <div className={styles.orgTitleSection}>
                                <div className={styles.orgLabel}>{formatYear(currentNode.name)}</div>
                                <div className={styles.orgTitle}>Year Overview</div>
                            </div>
                            <button className={styles.closeButtonWhite} onClick={handleClose}>×</button>
                        </div>

                        {/* Back navigation */}
                        {showYearBackButton && (
                            <div className={styles.backSection} onClick={handleBack}
                            onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleBack();
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 12L6 8L10 4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>{backText}</span>
                            </div>
                        )}

                        {/* Audit IDs Section */}
                        <div className={styles.themesSection}>
                            <div className={styles.themesHeader}>Audit IDs ({currentNode.children?.length || 0})</div>
                            <div className={styles.themesList}>
                                {currentNode.children?.map((child: any, index: number) => (
                                    <div key={index} className={styles.themeCard} onClick={() => handleItemClick(child)}
                                    onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleItemClick(child);
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                        <div className={styles.auditIconWrapper}>
                                            <img src={AuditsIcon} alt="Audit" style={{ width: '20px', height: '20px' }} />
                                        </div>
                                        <div className={styles.auditInfo}>
                                            <div className={styles.themeName}>{child.name}</div>
                                        </div>
                                        <svg className={styles.themeArrow} width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            }

            case 'AUDIT': {
                const compliantChild = currentNode.children?.find((c: any) => c.nodeType === 'COMPLIANT');
                const nonCompliantChild = currentNode.children?.find((c: any) => c.nodeType === 'NON_COMPLIANT');
                const compliantCount = parseInt(compliantChild?.name || '0');
                const nonCompliantCount = parseInt(nonCompliantChild?.name || '0');
                const showAuditBackButton = navigationStack.length > 1;

                return (
                    <div className={styles.content}>
                        {/* Audit Header */}
                        <div className={styles.orgHeader}>
                            <div className={styles.orgIconWrapper}>
                                <img src={AuditsIcon} alt="Audit" style={{ width: '24px', height: '24px' }} />
                            </div>
                            <div className={styles.orgTitleSection}>
                                <div className={styles.orgLabel}>{currentNode.name}</div>
                                <div className={styles.orgTitle}>Audit Details</div>
                            </div>
                            <button className={styles.closeButtonWhite} onClick={handleClose}>×</button>
                        </div>

                        {/* Back to Year */}
                        {showAuditBackButton && (
                            <div className={styles.backSection} onClick={handleBack}
                            onKeyDown={(e) => {
                                             if (e.key === "Enter" || e.key === " ") {
                                                 handleBack();
                                                }
                                              }}
                                             role="button"
                                             tabIndex={0}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 12L6 8L10 4" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>Back to Year</span>
                            </div>
                        )}

                        {/* Compliance Stats Cards */}
                        <div className={styles.complianceCards}>
                            <div className={styles.compliantCard}>
                                <div className={styles.compliantLabel}>Compliant</div>
                                <div className={styles.compliantValue}>{compliantCount}</div>
                            </div>
                            <div className={styles.nonCompliantCard}>
                                <div className={styles.nonCompliantLabel}>Non-Compliant</div>
                                <div className={styles.nonCompliantValue}>{nonCompliantCount}</div>
                            </div>
                        </div>
                    </div>
                );
            }

            default:
                return (
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <h3>{currentNode.name}</h3>
                            <button className={styles.closeButton} onClick={handleClose}>×</button>
                        </div>
                        <div className={styles.section}>
                            <p>No details available</p>
                        </div>
                    </div>
                );
        }
    };

    return (
        <>
            <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`} />
            <div className={`${styles.modal} ${isOpen ? styles.open : ''}`}>
                {renderContent()}
            </div>
        </>
    );
};

export default NodeDetailModal;
